import { useState, useEffect } from 'react';
import { Star, Facebook, Instagram, Heart, MessageCircle } from 'lucide-react';
import { getMetaFacebookReviews, getMetaFacebookPosts, getMetaInstagramFeed } from '@/lib/api';

function RatingStars(props) {
  var stars = [];
  for (var i = 1; i <= 5; i++) {
    var cn = i <= Math.round(props.rating)
      ? 'w-7 h-7 text-yellow-500 fill-yellow-500'
      : 'w-7 h-7 text-zinc-600';
    stars.push(<Star key={i} className={cn} />);
  }
  return <div className="flex justify-center gap-1 mb-3">{stars}</div>;
}

function SmallStars(props) {
  var stars = [];
  for (var i = 1; i <= 5; i++) {
    var cn = i <= props.rating
      ? 'w-3 h-3 text-yellow-500 fill-yellow-500'
      : 'w-3 h-3 text-zinc-600';
    stars.push(<Star key={i} className={cn} />);
  }
  return <div className="flex gap-0.5">{stars}</div>;
}

function ReviewCard(props) {
  var r = props.review;
  return (
    <div className="glass p-5 border border-zinc-800 bg-zinc-900/50">
      <div className="flex items-center gap-3 mb-3">
        {r.photo ? <img src={r.photo} alt="" className="w-10 h-10 rounded-full" referrerPolicy="no-referrer" loading="lazy" /> : null}
        <div>
          <p className="text-white font-medium text-sm">{r.name}</p>
          <SmallStars rating={r.rating} />
        </div>
      </div>
      {r.text ? <p className="text-zinc-400 text-sm">{r.text}</p> : null}
    </div>
  );
}

export function FacebookReviews() {
  var defaultState = { overall_rating: 0, rating_count: 0, reviews: [], page_name: '' };
  const [fbData, setFbData] = useState(defaultState);
  const [loaded, setLoaded] = useState(false);

  useEffect(function() {
    getMetaFacebookReviews().then(function(res) {
      var d = res.data;
      if (d && d.success) {
        setFbData(d);
      }
      setLoaded(true);
    }).catch(function() { setLoaded(true); });
  }, []);

  if (!loaded) return null;

  var pageName = fbData.page_name || 'Naki Tune 2.0';
  var reviewList = fbData.reviews || [];
  var ratingText = fbData.rating_count > 0
    ? 'Based on ' + fbData.rating_count + ' reviews'
    : 'Be the first to leave a review!';

  var reviewCards = reviewList.map(function(review, idx) {
    return <ReviewCard key={idx} review={review} />;
  });

  return (
    <section className="relative z-10 py-16 border-t border-zinc-800">
      <div className="container mx-auto px-4">
        <div className="flex items-center justify-center gap-3 mb-8">
          <Facebook className="w-6 h-6 text-blue-500" />
          <h2 className="font-['Barlow_Condensed'] text-3xl md:text-4xl font-bold uppercase tracking-tight text-white">
            Reviews
          </h2>
        </div>
        <div className="max-w-4xl mx-auto">
          <div className="glass p-8 border border-zinc-800 bg-zinc-900/50 text-center mb-6" data-testid="fb-rating-card">
            <h3 className="font-['Barlow_Condensed'] text-xl font-bold uppercase text-white mb-3">
              {pageName} on Facebook
            </h3>
            <RatingStars rating={fbData.overall_rating} />
            {fbData.overall_rating > 0 ? (
              <p className="text-green-500 font-['JetBrains_Mono'] text-2xl font-bold mb-1">
                {fbData.overall_rating.toFixed(1)} / 5
              </p>
            ) : null}
            <p className="text-zinc-500 text-sm">{ratingText}</p>
          </div>
          {reviewList.length > 0 ? (
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">{reviewCards}</div>
          ) : (
            <div className="text-center">
              <a
                href="https://www.facebook.com/profile.php?id=61567824720657"
                target="_blank"
                rel="noopener noreferrer"
                className="text-blue-500 hover:text-blue-400 text-sm"
                data-testid="fb-page-link"
              >
                Visit our Facebook Page
              </a>
            </div>
          )}
        </div>
      </div>
    </section>
  );
}

function IgPost(props) {
  var p = props.post;
  return (
    <a
      href={p.permalink}
      target="_blank"
      rel="noopener noreferrer"
      className="aspect-square overflow-hidden border border-zinc-800 relative group"
    >
      <img
        src={p.url}
        alt={p.caption || 'Instagram post'}
        className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-300"
        referrerPolicy="no-referrer"
        loading="lazy"
      />
      <div className="absolute inset-0 bg-black/60 opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center gap-4 text-white text-sm">
        {p.likes > 0 ? (
          <span className="flex items-center gap-1">
            <Heart className="w-4 h-4" /> {p.likes}
          </span>
        ) : null}
        {p.comments > 0 ? (
          <span className="flex items-center gap-1">
            <MessageCircle className="w-4 h-4" /> {p.comments}
          </span>
        ) : null}
      </div>
    </a>
  );
}

function FbPostCard({ post }) {
  const truncate = (t, n) => (t && t.length > n ? t.slice(0, n) + '…' : t || '');
  const dateStr = post.created_time
    ? new Date(post.created_time).toLocaleDateString('en-NZ', { day: 'numeric', month: 'short', year: 'numeric' })
    : '';
  return (
    <a
      href={post.permalink}
      target="_blank"
      rel="noopener noreferrer"
      className="block border border-zinc-800 bg-zinc-900/50 hover:border-blue-600 transition-colors overflow-hidden group"
      data-testid={`fb-post-${post.id}`}
    >
      {post.image ? (
        <div className="aspect-video overflow-hidden bg-zinc-950">
          <img
            src={post.image}
            alt={truncate(post.message, 60)}
            className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
            referrerPolicy="no-referrer"
            loading="lazy"
          />
        </div>
      ) : null}
      <div className="p-4 space-y-2">
        <div className="flex items-center gap-2 text-xs text-zinc-500 uppercase tracking-wider">
          <Facebook className="w-3 h-3 text-blue-500" />
          <span>{dateStr}</span>
        </div>
        <p className="text-zinc-300 text-sm line-clamp-3">{truncate(post.message, 200)}</p>
      </div>
    </a>
  );
}

export function FacebookPosts() {
  const [posts, setPosts] = useState([]);
  const [loaded, setLoaded] = useState(false);

  useEffect(() => {
    getMetaFacebookPosts(6)
      .then((res) => {
        const d = res.data;
        if (d && d.success) setPosts(d.posts || []);
      })
      .catch(() => {})
      .finally(() => setLoaded(true));
  }, []);

  if (!loaded) return null;
  if (posts.length === 0) return null;

  return (
    <section className="relative z-10 py-16 border-t border-zinc-800" data-testid="facebook-posts-section">
      <div className="container mx-auto px-4">
        <div className="flex items-center justify-center gap-3 mb-8">
          <Facebook className="w-6 h-6 text-blue-500" />
          <h2 className="font-['Barlow_Condensed'] text-3xl md:text-4xl font-bold uppercase tracking-tight text-white">
            Facebook Feed
          </h2>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4 max-w-6xl mx-auto">
          {posts.map((post) => (
            <FbPostCard key={post.id} post={post} />
          ))}
        </div>
      </div>
    </section>
  );
}

export function InstagramFeed() {
  const [igPosts, setIgPosts] = useState([]);

  useEffect(function() {
    getMetaInstagramFeed().then(function(res) {
      var d = res.data;
      if (d && d.success) {
        var posts = d.posts || [];
        setIgPosts(posts);
      }
    }).catch(function() {});
  }, []);

  var postElements = igPosts.map(function(post) {
    return <IgPost key={post.id} post={post} />;
  });

  return (
    <section className="relative z-10 py-16 border-t border-zinc-800">
      <div className="container mx-auto px-4">
        <div className="flex items-center justify-center gap-3 mb-8">
          <Instagram className="w-6 h-6 text-pink-500" />
          <h2 className="font-['Barlow_Condensed'] text-3xl md:text-4xl font-bold uppercase tracking-tight text-white">
            Instagram
          </h2>
        </div>
        {igPosts.length > 0 ? (
          <div className="grid grid-cols-3 gap-2 max-w-4xl mx-auto">{postElements}</div>
        ) : (
          <div className="text-center">
            <p className="text-zinc-500 mb-4 text-sm">Instagram feed coming soon</p>
          </div>
        )}
        <div className="text-center mt-4">
          <a
            href="https://www.instagram.com/nakitune2.0/"
            target="_blank"
            rel="noopener noreferrer"
            className="text-pink-500 hover:text-pink-400 text-sm"
            data-testid="ig-profile-link"
          >
            Follow @nakitune2.0 on Instagram
          </a>
        </div>
      </div>
    </section>
  );
}
